import styled from 'styled-components';

import { Card } from '@/ui/Card';
import { Skeleton } from '@/ui/Skeleton';
import { makeNamespacedComponent } from '@/ui/utils';
import {
  LearningCardDisabledContentDimmer,
  LearningCardMeta,
  LearningCardMetaProps,
} from '@/administration/pages/Journey/common/atoms/LearningCard/LearningCardMeta';
import {
  LearningCardProgress,
  LearningCardProgressProps,
} from '@/administration/pages/Journey/common/atoms/LearningCard/LearningCardProgress';
import { Icon } from '@/ui/Icon';
import SomethingWentWrongImage from '@/assets/something-went-wrong.svg';
import {
  RLearningContentContentType,
  RLearningContentLocationData,
  RLearningContentProvider,
  RLearningItemAvailabilityType,
  RLearningType,
} from '@/store/v2';
import { ProviderIcon } from '@/component/ProviderIcon/ProviderIcon';
import { getLocalProviderFromRemoteProvider, providerTitle } from '@/store/v2/provider';

export enum LearningCardError {
  UNAVAILABLE = 'UNAVAILABLE',
}

export type LearningCardVariant = 'default' | 'compact';

export type LearningCardProps = {
  objectId: string;
  id?: number;
  imageUrl?: string;
  type?: string;
  title?: string;
  totalEffort?: number;
  totalLearningItems?: number;
  disabled?: boolean;
  locked?: boolean;
  isOptional?: boolean;
  availabilityType?: RLearningItemAvailabilityType;
  realms?: string[];
  availabilityScheduledAt?: Date;
  availabilityScheduledAtTimezone?: number;
  locations?: Omit<RLearningContentLocationData, '__typename'>[];
  description?: string;
  error?: LearningCardError;
  progress?: LearningCardProgressProps['progress'];
  isInPreviewModal?: boolean;
  provider?: RLearningContentProvider;
  variant?: LearningCardVariant;
  launchPath?: string;
} & LearningCardMetaProps;

const RootStyled = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const ImageBody = styled.div<{ variant?: LearningCardVariant }>`
  flex: 1 0 auto;
  position: relative;
  width: ${({ variant }) => (variant === 'compact' ? '96px' : '180px')};
  height: ${({ variant }) => (variant === 'compact' ? '64px' : '120px')};
  max-width: ${({ variant }) => (variant === 'compact' ? '96px' : '180px')};
  max-height: ${({ variant }) => (variant === 'compact' ? '64px' : '120px')};
  border-radius: 4px;
`;

const ImageStyled = styled.img<{ variant?: LearningCardVariant }>`
  width: ${({ variant }) => (variant === 'compact' ? '96px' : '180px')};
  height: ${({ variant }) => (variant === 'compact' ? '64px' : '120px')};
  object-fit: cover;
  border-radius: 4px;
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  color: ${({ theme }) => theme.colors.neutral.background};

  background: linear-gradient(0deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.4) 100%);

  border-radius: 4px;
`;

const ContentStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 0 1 auto;
  min-width: 0;
`;

const HeadingStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardTitleStyled = styled(Card.Title)<{ variant?: LearningCardVariant }>`
  margin: 0;
  max-width: ${({ variant }) => (variant === 'compact' ? '200px' : '600px')};
`;

const MetaWrapStyled = styled.div`
  display: flex;
  gap: 16px;
`;

const ProviderStyled = styled.div`
  ${({ theme }) => theme.typography.caption};
  color: ${({ theme }) => theme.colors.neutral.onBackground};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LearningCardUnavailable = (): JSX.Element => {
  return (
    <RootStyled>
      <ImageBody>
        <ImageStyled src={SomethingWentWrongImage} alt="" />
      </ImageBody>
      <ContentStyled>
        <HeadingStyled>
          <CardTitleStyled>Learning item not available</CardTitleStyled>
        </HeadingStyled>
      </ContentStyled>
    </RootStyled>
  );
};

const LearningCard = ({
  imageUrl,
  title,
  type,
  totalEffort,
  totalLearningItems,
  level,
  progress,
  error,
  disabled,
  locked,
  locations,
  availabilityScheduledAt,
  availabilityScheduledAtTimezone,
  isOptional,
  provider,
  variant = 'default',
}: LearningCardProps): JSX.Element => {
  if (error === LearningCardError.UNAVAILABLE) {
    return <LearningCardUnavailable />;
  }

  const shouldShowIcon = locked || progress?.isCompleted;
  const isEvent = [RLearningType.LiveEvent, RLearningType.Seminar, RLearningType.Webinar].includes(type as RLearningType);

  const hasProgress = !!progress && !isEvent;

  return (
    <RootStyled>
      {imageUrl && (
        <ImageBody variant={variant}>
          <ImageStyled src={imageUrl} variant={variant} />
          {shouldShowIcon && (
            <ImageOverlay>
              <Icon icon={['far', progress?.isCompleted ? 'check' : 'lock']} size="2x" />
            </ImageOverlay>
          )}
        </ImageBody>
      )}
      <ContentStyled>
        <LearningCardDisabledContentDimmer $disabled={disabled}>
          {provider && (
            <ProviderStyled>
              <ProviderIcon provider={getLocalProviderFromRemoteProvider(provider) || ''} />
              {providerTitle(provider)}
            </ProviderStyled>
          )}
          <HeadingStyled>
            <CardTitleStyled>{title}</CardTitleStyled>
          </HeadingStyled>
          {hasProgress && (
            <LearningCardProgress
              progress={progress}
              totalEffort={totalEffort}
              totalLearningItems={totalLearningItems}
              timeProgressHidden={type === RLearningContentContentType.LiveEvent}
            />
          )}
        </LearningCardDisabledContentDimmer>
        {!hasProgress && (
          <LearningCardMeta
            type={type}
            variant={variant}
            level={level}
            totalEffort={totalEffort}
            totalLearningItems={totalLearningItems}
            locations={locations}
            isOptional={isOptional}
            availabilityScheduledAt={availabilityScheduledAt}
            availabilityScheduledAtTimezone={availabilityScheduledAtTimezone}
            disabled={disabled}
          />
        )}
      </ContentStyled>
    </RootStyled>
  );
};

const LoadingState = (): JSX.Element => (
  <RootStyled>
    <Skeleton width={96} height={64} />
    <ContentStyled>
      <HeadingStyled>
        <Skeleton width={60} height={16} />
        <Skeleton width={250} height={24} />
      </HeadingStyled>
      <MetaWrapStyled>
        <Skeleton width={70} height={16} />
        <Skeleton width={70} height={16} />
      </MetaWrapStyled>
    </ContentStyled>
  </RootStyled>
);

const _LearningCard = makeNamespacedComponent(LearningCard, {
  LoadingState,
});

export { _LearningCard as LearningCard };
